import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateAssessmentStatus } from "../../../../../services";
import { RootState } from "../../../../../store";
import { ASSESMENT_STATUS } from "@asez/shared";
import { apiService } from "../../../services/apiService";

export const useStepper = ({
  activeStep,
  handleStepChange,
}: {
  activeStep: number;
  handleStepChange: (arg: number) => void;
}) => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();

  const [completed, setCompleted] = useState<number[]>([]);
  const [isToastOpen, setToastOpen] = useState<boolean>(false);

  const stepOne = useSelector((state: RootState) => state.stepOne);
  const stepTwo = useSelector((state: RootState) => state.stepTwo);
  const stepFour = useSelector((state: RootState) => state.stepFour);

  const stepNumberToApiMap: Record<number, () => void> = {
    1: async () => {
      const addRequirementsBody = {
        id: assessmentId as string,
        name: stepOne.title,
        role: stepOne.jobDesignation,
        yearsOfExperience: {
          min: stepOne.experience[0],
          max: stepOne.experience[1],
        },
        expertiseInSkills: stepOne.skills,
      };
      await apiService.addRequirements(addRequirementsBody);
    },
    2: async () => {
      const saveReviewedRequirementsBody = {
        assessmentId: assessmentId as string,
        skillsDistribution:
          stepTwo.skillsDistribution && stepTwo.skillsDistribution.length > 0
            ? stepTwo.skillsDistribution
            : null,
        totalQuestions: stepTwo.totalQuestions,
      };
      await apiService.saveReviewedRequirements(saveReviewedRequirementsBody);
    },
    3: async () => {},
    4: async () => {
      const addTimingBody = {
        scheduleSetting: stepFour.scheduleSetting,
        timing: stepFour.timing,
        proctoring: stepFour.proctoring,
        selectedQuestions: stepFour.selectedQuestions,
        customQuestions: stepFour.customQuestions,
      };
      await apiService.addTiming(addTimingBody);
    },
    5: () => {},
    6: async () => {
      await updateAssessmentStatus({
        id: assessmentId as string,
        status: ASSESMENT_STATUS.ACTIVE,
      });
      navigate("/assessments");
    },
  };

  const handlePrev = () => {
    handleStepChange(activeStep - 1);
    setCompleted((prevCompleted) =>
      prevCompleted.filter((step) => step !== activeStep - 1)
    );
  };

  const validateCurrentStep = () => {
    switch (activeStep) {
      case 1:
        return stepOne.title && stepOne.jobDesignation && stepOne.skills;
      default:
        return true;
    }
  };

  const handleApiCalls = async (stepNumber: number) => {
    try {
      await stepNumberToApiMap[stepNumber]();
    } catch (err) {
      console.error(`API call failed for step ${stepNumber}: `, err);
    }
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) {
      setToastOpen(true);
      return;
    }

    if (!completed.includes(activeStep - 1)) {
      setCompleted((prevCompleted) => [...prevCompleted, activeStep - 1]);
    }

    handleStepChange(activeStep + 1);

    await handleApiCalls(activeStep);
  };

  useEffect(() => {
    const newCompleted: number[] = [];
    for (let i = 0; i < activeStep - 1; i++) {
      newCompleted.push(i);
    }
    setCompleted(newCompleted);
  }, [activeStep]);

  return {
    completed,
    handlePrev,
    handleNext,
    isToastOpen,
    setToastOpen,
    title: stepOne.title,
    status: stepOne.status,
  };
};
